// OneDrive:IgnoreCodeCoverage

import Promise from '@ms/odsp-utilities/lib/async/Promise';
import XHRPromise from '@ms/odsp-utilities/lib/xhr/XHRPromise';
import { ITokenProvider, ISessionToken } from '../../base/tokenProvider/ITokenProvider';
import { IDataRequestor, IDataRequestorSendOptions } from './IDataRequestor';
import { Api as ApiEvent, IApiEndSchema, ResultTypeEnum } from '@ms/odsp-utilities/lib/logging/events/Api.event';
import Uri from '@ms/odsp-utilities/lib/uri/Uri';
import { IErrorResponse, GraphError, getQosExtraDataFromError, getResultCodeFromError } from '../../models/error/Error';
import { IUrlProvider } from '../dataRequestor/IDataRequestor';

export { IDataRequestor };

/**
 * Params passed to a data requestor instance to control its overall behavior.
 */
export interface IDataRequestorParams {
    alwaysSendContentType?: boolean;
}

export interface IDataRequestorDependencies {
    sessionManagementProvider?: ITokenProvider;
    urlDataSource?: IUrlProvider;
    apiEvent?: typeof ApiEvent;
}

export class DataRequestor implements IDataRequestor {
    private _sessionManagementProvider: ITokenProvider | undefined;
    private _apiName: string | undefined;
    private _apiBaseUrl: string | undefined;
    private _apiEvent: typeof ApiEvent;
    private _alwaysSendContentType: boolean;

    constructor(params: IDataRequestorParams, dependencies: IDataRequestorDependencies) {
        const {
            alwaysSendContentType = false
        } = params;

        const {
            sessionManagementProvider,
            urlDataSource,
            apiEvent = ApiEvent
        } = dependencies;

        this._sessionManagementProvider = sessionManagementProvider;
        this._alwaysSendContentType = alwaysSendContentType;
        this._apiEvent = apiEvent;

        this._apiName = urlDataSource && typeof (urlDataSource.getApiName) === "function" ?
            urlDataSource.getApiName() :
            "Vroom";

        this._apiBaseUrl = urlDataSource ?
            (typeof (urlDataSource.getApiBaseUrl) === "function" ?
                urlDataSource.getApiBaseUrl() :
                urlDataSource.getVroomApiBaseUrl()) :
            undefined;
    }

    public send<T>(options: IDataRequestorSendOptions<T>): Promise<T> {
        const {
            headers = {},
            path,
            apiName,
            requestType = 'GET',
            disableLogging = false,
            timeout,
            onProgress,
            onUploadProgress,
            qosExtraData,
            apiVersion = 'v2.0',
            needsAuthorization = true,
            useAuthorizationHeaders = false,
            accessToken,
            postProcessQos,
            breakCache
        } = options;

        const sessionManagementProvider = this._sessionManagementProvider;

        const getAccessToken = needsAuthorization && (accessToken || sessionManagementProvider && sessionManagementProvider.getToken(apiVersion).catch((error: Error) => {
            // Treat auth failures as a client-side service interruption.
            return Promise.reject(new GraphError(error, {
                status: 0,
                response: {
                    error: {
                        code: 'authFailure'
                    }
                }
            }));
        })) || undefined;

        return Promise.resolve(getAccessToken).then((sessionToken: ISessionToken) => {
            const apiBaseUrl = this._apiBaseUrl;

            // Don't pass a content-type header if no postData. Otherwise we are stuck with CORS pre-flight on all requests.
            let requestHeaders: { [key: string]: string } = {
                'Accept': 'application/json',
                ...(options.postData || this._alwaysSendContentType ? {
                    'Content-Type': 'application/json'
                } : {}),
                ...headers
            };

            let apiUri: Uri;

            if (sessionToken && sessionToken.tokenUrl && path.indexOf("/drive/") === 0) {
                // replace the generic "/drive/" with our specific token url
                apiUri = new Uri(`${sessionToken.tokenUrl}/${path.substring(7)}`);
            } else {
                apiUri = new Uri(path);
                if (apiBaseUrl && !apiUri.getScheme()) {
                    apiUri = new Uri(apiBaseUrl + path);
                }
            }

            // save the url to log before we add sensitive tokens
            let apiUrlToLog = apiUri.toString();

            if (sessionToken) {
                const {
                    accessToken: sessionAccessToken,
                    proofToken: proofToken,
                    wlidToken
                } = sessionToken;

                if (wlidToken) {
                    requestHeaders = {
                        ...requestHeaders,
                        'Authorization': `wlid1.1 ${wlidToken}`
                    };
                } else if (useAuthorizationHeaders) {
                    if (sessionAccessToken) {
                        requestHeaders = {
                            ...requestHeaders,
                            'Authorization': `bearer ${sessionAccessToken}`
                        };
                    }

                    if (proofToken) {
                        requestHeaders = {
                            ...requestHeaders,
                            'X-PROOF_TOKEN': proofToken
                        };
                    }
                } else {
                    if (sessionAccessToken) {
                        // We attach the access token as a query parameter to every request.
                        // We could also pass it in through the 'Authorization: bearer' header,
                        // but that would result in an extra CORS preflight request for every
                        // unique path.
                        apiUri.setQueryParameter('access_token', sessionAccessToken);
                    }

                    if (proofToken) {
                        // ODB scenarios use proof tokens
                        apiUri.setQueryParameter('prooftoken', proofToken);
                    }
                }
            }

            if (breakCache) {
                apiUri.setQueryParameter('r', Math.random().toFixed(8));
            }

            // Update prefer headers to be Vault aware
            // Only ODC technically needs this; will just no-op elsewhere.
            // Check for the 'prefer' header in a case-insensitive manner
            let preferHeader = '';
            let modifiedHeaders: { [key: string]: string } = {};
            for (let key in requestHeaders) {
                if (key.toLocaleLowerCase() === 'prefer') {
                    preferHeader = requestHeaders[key] + ', ';
                } else {
                    modifiedHeaders = {
                        ...modifiedHeaders,
                        [key]: requestHeaders[key]
                    };
                }
            }

            // Rebuild the new headers with the modified 'Prefer' header
            requestHeaders = {
                ...modifiedHeaders,
                'Prefer': preferHeader + 'Include-Feature=Vault'
            };

            let apiEvent: ApiEvent;

            if (!disableLogging) {
                // wipe access_token, prooftoken, tempauth
                apiUrlToLog = apiUrlToLog.replace(/(?:(?:access_|proof)token|tempauth|authkey)=[^&]+/gi, '');
                // wipe anything JWT-shaped
                apiUrlToLog = apiUrlToLog.replace(/(?:eyJ[A-Za-z0-9_-]+(?:\.|%2E|%252E)){2}(?:[A-Za-z0-9_-]+)/gi, '$REDACTED$');

                apiEvent = new this._apiEvent({
                    url: apiUrlToLog,
                    name: `${this._apiName}DataRequest.${apiName}`,
                    extraData: qosExtraData
                });
            }

            return XHRPromise.start({
                url: apiUri.toString(),
                headers: requestHeaders,
                method: requestType,
                json: options.postData || '',
                requestTimeoutInMS: timeout,
                withCredentials: false,
                onProgress: onProgress,
                onUploadProgress: onUploadProgress
            }).then((request: XMLHttpRequest) => {
                if (apiEvent) {
                    apiEvent.end({
                        resultType: ResultTypeEnum.Success
                    });
                }

                if (options.parseResponse) {
                    return options.parseResponse(request);
                }

                if (request.responseText.length === 0) {
                    return null;
                }

                return <T>JSON.parse(request.responseText);
            }, (request: XMLHttpRequest | Error) => {
                let response: IErrorResponse;

                let apiEndSchema: IApiEndSchema;

                let error: Error;

                if (request instanceof Error) {
                    const resultType = Promise.isCanceled(request) ? ResultTypeEnum.ExpectedFailure : ResultTypeEnum.Failure;

                    apiEndSchema = {
                        error: request.message,
                        resultType: resultType
                    };

                    error = request;
                } else {
                    if (request.responseText) {
                        try {
                            response = <IErrorResponse>JSON.parse(request.responseText);
                        } catch (error) {
                            // no-op
                        }
                    }

                    const {
                        status,
                        statusText
                    } = request;

                    const {
                        error: {
                            message = '',
                            innererror: {
                                stackTrace = '',
                                debugMessage = ''
                            } = {}
                        } = {}
                    } = response || {};

                    error = new GraphError(new Error(message), {
                        response: response,
                        status: status,
                        request: request
                    });

                    const resultCode = getResultCodeFromError(error);

                    apiEndSchema = {
                        resultType: ResultTypeEnum.Failure,
                        error: message,
                        resultCode: resultCode,
                        extraData: {
                            ...(qosExtraData || {}),
                            ...getQosExtraDataFromError(error),
                            stack: stackTrace,
                            httpStatus: status,
                            httpStatusText: statusText,
                            requestId: request.getResponseHeader("request-id") || request.getResponseHeader("sprequestguid"),
                            debugMessage: debugMessage,
                            accessTokenLength: (sessionToken && sessionToken.accessToken) ? sessionToken.accessToken.length : -1,
                            urlLength: apiUri.toString().length
                        }
                    };
                }

                if (postProcessQos) {
                    apiEndSchema = postProcessQos(apiEndSchema, error);
                }

                if (apiEvent) {
                    apiEvent.end(apiEndSchema);
                }

                return Promise.reject(error);
            });
        });
    }
}

export default DataRequestor;
